import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { revealProps } from "@/components/Reveal";
import { Bike, Store, ArrowRight, Star, Gift, Clock, MapPin, Phone, Mail, Truck, Award, Leaf, Navigation } from "lucide-react";
import api, { money } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import BranchSelect from "@/components/BranchSelect";
import BranchSuggestBar from "@/components/BranchSuggestBar";
import { dayWindow, nextOpening, windowLabel } from "@/lib/hours";
import { Stars } from "@/components/Brand";
import { addOfferToCart } from "@/lib/offers";
import { useSeo, useJsonLd } from "@/lib/seo";

const Section = ({ title, subtitle, children, action, id }) => (
  <motion.section id={id} className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14" {...revealProps()}>
    <div className="flex items-end justify-between mb-6">
      <div>
        {subtitle && <p className="text-metros-red font-bold text-xs uppercase tracking-widest mb-1">{subtitle}</p>}
        <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight text-metros-black">{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </motion.section>
);

const DIVIDER_STYLE = { background: "linear-gradient(to right, transparent 0%, rgba(225,29,42,0.15) 12.5%, rgba(225,29,42,0.55) 37.5%, rgba(10,10,10,0.55) 62.5%, rgba(10,10,10,0.15) 87.5%, transparent 100%)" };
const Divider = () => (
  <div className="max-w-6xl mx-auto px-6 md:px-10"><div className="h-[3px] w-full rounded-full" style={DIVIDER_STYLE} /></div>
);

const DEFAULT_ORDER = ["offers", "explore_menu", "popular", "best_sellers", "why_choose", "rewards", "reviews", "about", "final_cta"];

export default function Home() {
  const { openOrderType } = useOutletContext();
  const { settings } = useSettings();
  const { user } = useAuth();
  const { setOrderType, selectedBranch, branchStatus, setScheduleIntent, addItem } = useCart();
  const [draft, setDraft] = useState(null);
  const navigate = useNavigate();
  useSeo({
    title: "Metros Fried Chicken | Takeaway & Food Delivery in Epsom",
    description: "Order fresh fried chicken, chicken burgers, wings and sides from Metros in Epsom, Surrey. Fast local takeaway and food delivery — order online for collection or delivery.",
    keywords: "fried chicken, chicken burger, chicken wings, sides, takeaway Epsom, food delivery Epsom, Epsom Surrey, Metros fried chicken",
    path: "/",
    image: "https://mymetros.co.uk/logo.png",
  });
  const [popular, setPopular] = useState([]);
  const [best, setBest] = useState([]);
  const [cats, setCats] = useState([]);
  const [offers, setOffers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [modal, setModal] = useState(null);
  const [branchOpen, setBranchOpen] = useState(false);
  const [switchDismissed, setSwitchDismissed] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const [catProducts, setCatProducts] = useState([]);
  const [activeOffer, setActiveOffer] = useState(0);
  const [allProducts, setAllProducts] = useState([]);
  const offersRef = useRef(null);

  // Restaurant + review structured data (JSON-LD) built from live settings & reviews.
  const structuredData = useMemo(() => {
    if (!settings) return null;
    const dayMap = { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday" };
    const hours = Object.entries(settings.opening_hours || {})
      .filter(([, v]) => v && !v.closed && v.open && v.close)
      .map(([d, v]) => ({ "@type": "OpeningHoursSpecification", dayOfWeek: `https://schema.org/${dayMap[d]}`, opens: v.open, closes: v.close }));
    const ratings = reviews.map((r) => r.rating).filter(Boolean);
    const avg = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null;
    const data = {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "@id": "https://mymetros.co.uk/#restaurant",
      name: "Metros Fried Chicken",
      description: "Fried chicken takeaway in Epsom, Surrey serving fresh fried chicken, chicken burgers, wings and sides for collection and delivery.",
      url: "https://mymetros.co.uk/",
      image: "https://mymetros.co.uk/logo.png",
      servesCuisine: ["Fried Chicken", "Fast Food", "Burgers"],
      priceRange: "££",
      areaServed: "Epsom, Surrey",
      hasMenu: "https://mymetros.co.uk/menu",
      address: { "@type": "PostalAddress", streetAddress: settings.address || "", addressLocality: "Epsom", addressRegion: "Surrey", addressCountry: "GB" },
    };
    if (settings.phone) data.telephone = settings.phone;
    if (settings.email) data.email = settings.email;
    if (hours.length) data.openingHoursSpecification = hours;
    if (avg) data.aggregateRating = { "@type": "AggregateRating", ratingValue: avg.toFixed(1), reviewCount: ratings.length, bestRating: 5 };
    if (reviews.length) data.review = reviews.slice(0, 5).map((r) => ({ "@type": "Review", author: { "@type": "Person", name: r.name }, reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 }, reviewBody: r.comment }));
    return data;
  }, [settings, reviews]);
  useJsonLd("restaurant", structuredData);

  const s = draft || settings || {};
  const bq = selectedBranch ? `&branch_id=${selectedBranch.id}` : "";
  const hp = s.homepage || {};
  const offerIds = Array.isArray(hp.offers_ids) ? hp.offers_ids : [];
  const popularIds = Array.isArray(hp.popular_ids) ? hp.popular_ids : [];
  const bestIds = Array.isArray(hp.best_ids) ? hp.best_ids : [];

  const selectCat = useCallback(async (name) => {
    setActiveCat(name);
    try {
      const { data } = await api.get(`/menu/products?category=${encodeURIComponent(name)}${bq}`);
      setCatProducts(data.slice(0, 8));
    } catch { setCatProducts([]); }
  }, [bq]);

  useEffect(() => { if (cats.length && !activeCat) selectCat(cats[0].name); }, [cats, activeCat, selectCat]);

  useEffect(() => {
    api.get(`/menu/products?popular=true${bq}`).then(({ data }) => setPopular(data.slice(0, 8))).catch(() => {});
    api.get(`/menu/products?sort=popular${bq}`).then(({ data }) => setBest(data.slice(0, 4))).catch(() => {});
    api.get("/menu/categories").then(({ data }) => setCats(data)).catch(() => {});
    api.get(`/offers${selectedBranch ? `?branch_id=${selectedBranch.id}` : ""}`).then(({ data }) => setOffers(data)).catch(() => {});
    api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => {});
  }, [bq]);

  // Homepage carousels can feature specific offers/products chosen in the builder.
  useEffect(() => {
    if (!popularIds.length && !bestIds.length) return;
    const q = selectedBranch ? `?branch_id=${selectedBranch.id}` : "";
    api.get(`/menu/products${q}`).then(({ data }) => setAllProducts(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popularIds.join(","), bestIds.join(","), selectedBranch]);

  // Homepage Builder live preview: when rendered inside the builder iframe, accept draft settings.
  useEffect(() => {
    const onMsg = (e) => { if (e && e.data && e.data.type === "metros-hp-preview") setDraft(e.data.settings || null); };
    window.addEventListener("message", onMsg);
    try { if (window.parent && window.parent !== window) window.parent.postMessage({ type: "metros-hp-preview-ready" }, "*"); } catch (_) {}
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const startOrder = useCallback((type) => { setScheduleIntent(false); setOrderType(type); navigate("/menu"); }, [setScheduleIntent, setOrderType, navigate]);
  const scheduleOrder = useCallback(() => { setScheduleIntent(true); navigate("/menu"); }, [setScheduleIntent, navigate]);
  const hero = s.hero || {};
  const heroBg = hero.bg_color || "#0A0A0A";
  const heroOpacity = hero.bg_opacity != null ? hero.bg_opacity : 80;
  const heroAlphaHex = Math.round((Math.max(0, Math.min(100, heroOpacity)) / 100) * 255).toString(16).padStart(2, "0");
  const heroBgAlpha = /^#[0-9a-fA-F]{6}$/.test(heroBg) ? `${heroBg}${heroAlphaHex}` : heroBg;
  const rewards = s.rewards || {};
  const storeW = dayWindow(s, "collection");
  const delivW = dayWindow(s, "delivery");
  const nextOpen = nextOpening(s, "collection");
  const offersById = {}; offers.forEach((o) => { offersById[o.id] = o; });
  const prodById = {}; allProducts.forEach((p) => { prodById[p.id] = p; });
  const shownOffers = offerIds.length ? offerIds.map((id) => offersById[id]).filter(Boolean) : offers.slice(0, 3);
  const popularList = popularIds.length ? popularIds.map((id) => prodById[id]).filter(Boolean) : popular.slice(0, 4);
  const bestList = bestIds.length ? bestIds.map((id) => prodById[id]).filter(Boolean) : best;

  const scrollToOffer = useCallback((i) => {
    const el = offersRef.current;
    if (!el || !el.children[i]) return;
    el.scrollTo({ left: el.children[i].offsetLeft - el.children[0].offsetLeft, behavior: "smooth" });
  }, []);

  const onOffersScroll = useCallback(() => {
    const el = offersRef.current;
    if (!el) return;
    const cards = [...el.children];
    let idx = 0, closest = Infinity;
    cards.forEach((c, k) => {
      const d = Math.abs((c.offsetLeft - cards[0].offsetLeft) - el.scrollLeft);
      if (d < closest) { closest = d; idx = k; }
    });
    setActiveOffer(idx);
  }, []);

  useEffect(() => {
    if (shownOffers.length <= 1) return;
    const id = setInterval(() => {
      if (window.innerWidth >= 768) return; // auto-rotate only on mobile carousel
      const el = offersRef.current;
      if (!el) return;
      const next = (activeOffer + 1) % shownOffers.length;
      if (el.children[next]) el.scrollTo({ left: el.children[next].offsetLeft - el.children[0].offsetLeft, behavior: "smooth" });
    }, 4000);
    return () => clearInterval(id);
  }, [activeOffer, shownOffers.length]);

  // ---- Homepage layout config (super-admin Homepage Builder) ----
  const savedSecs = Array.isArray(hp.sections) ? hp.sections : [];
  const secByKey = {};
  savedSecs.forEach((x) => { if (x && x.key) secByKey[x.key] = x; });
  const orderedKeys = [
    ...savedSecs.map((x) => x && x.key).filter((k) => DEFAULT_ORDER.includes(k)),
    ...DEFAULT_ORDER.filter((k) => !secByKey[k]),
  ];
  const isVisible = (k) => (secByKey[k] ? secByKey[k].visible !== false : true);
  const tx = (k, field, def) => {
    const v = secByKey[k] && secByKey[k][field];
    return (v !== undefined && v !== null && v !== "") ? v : def;
  };

  const sectionNodes = {
    offers: shownOffers.length > 0 && (
      <Section title={tx("offers", "title", "Today's Offers")} subtitle={tx("offers", "subtitle", "Save more")}
        action={<Link to="/offers" className="text-sm font-semibold text-metros-red flex items-center gap-1">All offers <ArrowRight size={14} /></Link>}>
        <div ref={offersRef} onScroll={onOffersScroll} data-testid="offers-carousel" className="flex md:grid md:grid-cols-3 gap-4 md:gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth">
          {shownOffers.map((o) => (
            <div key={o.id} data-testid={`home-offer-${o.id}`} className="relative overflow-hidden rounded-2xl bg-metros-black text-white group cursor-pointer shrink-0 md:shrink w-[82%] sm:w-[55%] md:w-auto snap-center" onClick={() => navigate("/offers")}>
              <img src={o.image} alt={o.title} className="h-[120px] w-full object-cover opacity-60 group-hover:opacity-70 transition-opacity" />
              <div className="p-5 -mt-12 relative">
                <span className="bg-metros-red text-white text-xs font-bold uppercase px-2 py-1 rounded">{o.type}</span>
                <h3 className="font-heading font-extrabold text-xl mt-2">{o.title}</h3>
                <p className="text-sm text-gray-300">{o.description}</p>
                {o.discounted_price > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="line-through text-gray-500 text-sm">{money(o.original_price)}</span>
                    <span className="font-heading font-extrabold text-metros-red text-xl">{money(o.discounted_price)}</span>
                  </div>
                )}
                {o.product_ids && o.product_ids.length > 0 && (
                  <button data-testid={`home-offer-add-${o.id}`}
                    onClick={(e) => { e.stopPropagation(); addOfferToCart(o, { addItem, selectedBranch, navigate }); }}
                    className="mt-3 w-full bg-metros-red hover:bg-metros-redhover text-white rounded-full py-2.5 text-sm font-bold active:scale-95 transition-all">
                    Add to basket
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {shownOffers.length > 1 && (
          <div className="flex md:hidden justify-center gap-2 mt-4" data-testid="offers-dots">
            {shownOffers.map((_, i) => (
              <button key={i} data-testid={`offer-dot-${i}`} onClick={() => scrollToOffer(i)} aria-label={`Go to offer ${i + 1}`}
                className={`h-2 rounded-full transition-all ${activeOffer === i ? "w-6 bg-metros-red" : "w-2 bg-gray-300"}`} />
            ))}
          </div>
        )}
      </Section>
    ),
    explore_menu: (
      <>
        <Divider />
        <motion.section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14" {...revealProps()}>
          <div className="mb-5">
            <p className="text-metros-red font-bold text-xs uppercase tracking-widest mb-1">{tx("explore_menu", "subtitle", "Order Now")}</p>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight text-metros-black">
              {secByKey["explore_menu"] && secByKey["explore_menu"].title
                ? secByKey["explore_menu"].title
                : (<>Explore the <span className="text-metros-red">Menu</span></>)}
            </h2>
            <button data-testid="explore-view-all" onClick={() => navigate("/menu")}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-metros-red hover:gap-2.5 transition-all">
              View All <ArrowRight size={16} />
            </button>
          </div>
          <div className="flex gap-2.5 overflow-x-auto no-scrollbar mb-7 pb-1">
            {cats.map((c) => (
              <button key={c.id} data-testid={`home-cat-${c.slug}`} onClick={() => selectCat(c.name)}
                className={`whitespace-nowrap font-heading font-bold uppercase tracking-wide text-sm px-5 py-2.5 rounded-full border transition-all ${activeCat === c.name ? "bg-metros-red text-white border-metros-red shadow-md scale-105" : "bg-white text-metros-black border-gray-200 shadow-sm hover:border-metros-red hover:text-metros-red"}`}>
                {c.name}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-5">
            {catProducts.map((p) => <ProductCard key={p.id} product={p} onOpen={setModal} compact />)}
          </div>
        </motion.section>
      </>
    ),
    popular: popularList.length > 0 && (
      <>
        <Divider />
        <Section title={tx("popular", "title", "Popular Products")} subtitle={tx("popular", "subtitle", "Loved by locals")}
          action={<button onClick={() => navigate("/menu")} className="text-sm font-semibold text-metros-red flex items-center gap-1">View all <ArrowRight size={14} /></button>}>
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-5">
            {popularList.map((p) => <ProductCard key={p.id} product={p} onOpen={setModal} compact />)}
          </div>
        </Section>
      </>
    ),
    best_sellers: bestList.length > 0 && (
      <>
        <Divider />
        <Section title={tx("best_sellers", "title", "Best Sellers")} subtitle={tx("best_sellers", "subtitle", "The classics")}>
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-5">
            {bestList.map((p) => <ProductCard key={p.id} product={p} onOpen={setModal} compact />)}
          </div>
        </Section>
      </>
    ),
    why_choose: (
      <motion.section className="bg-[#F9F9F9] py-7" {...revealProps()}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <p className="text-metros-red font-bold text-xs uppercase tracking-widest mb-1">{tx("why_choose", "subtitle", "Why Choose METROS")}</p>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl mb-4">{tx("why_choose", "title", "Made for you, every time")}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {(s.why_choose || []).map((w, i) => {
              const Icons = [Leaf, Award, Truck, MapPin];
              const Ic = Icons[i % 4];
              return (
                <div key={w.title || i} className="bg-white rounded-2xl p-4 md:p-6 shadow-sm">
                  <div className="bg-metros-red/10 text-metros-red rounded-xl p-2.5 md:p-3 w-fit mb-2 md:mb-3"><Ic className="w-[18px] h-[18px] md:w-[22px] md:h-[22px]" /></div>
                  <h3 className="font-heading font-bold text-base md:text-lg">{w.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </motion.section>
    ),
    rewards: rewards.enabled && (
      <Section title={tx("rewards", "title", "METROS Rewards")} subtitle={tx("rewards", "subtitle", "Loyalty")}>
        <div className="rounded-3xl bg-metros-black text-white grain p-4 md:p-6 flex flex-col md:flex-row items-center gap-5">
          <div className="bg-metros-red rounded-2xl p-4"><Gift size={36} /></div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-heading font-extrabold text-2xl">Earn points every time you order</h3>
            <p className="text-gray-300 mt-2">Collect {rewards.points_per_pound} points per £1 spent. {rewards.reward_threshold} points = {money(rewards.reward_value)} off your next order.</p>
          </div>
          <button data-testid="rewards-cta" onClick={() => navigate(user ? "/account" : "/register")}
            className="bg-white text-metros-black rounded-full px-7 py-3.5 font-bold hover:bg-metros-red hover:text-white transition-colors">
            {user ? "View Rewards" : "Join Now"}
          </button>
        </div>
      </Section>
    ),
    reviews: reviews.length > 0 && (
      <Section title={tx("reviews", "title", "What locals say")} subtitle={tx("reviews", "subtitle", "Reviews")}
        action={<Link to="/contact" className="text-sm font-semibold text-metros-red">Leave a review</Link>}>
        <div className="flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth" data-testid="reviews-carousel">
          {reviews.slice(0, 8).map((r) => (
            <div key={r.id} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm shrink-0 w-[85%] sm:w-[46%] md:w-[32%] snap-center">
              <Stars rating={r.rating} />
              <p className="text-metros-black mt-3">“{r.comment}”</p>
              <p className="text-sm font-semibold mt-3">{r.name}</p>
            </div>
          ))}
        </div>
      </Section>
    ),
    about: (
      <motion.section className="max-w-7xl mx-auto px-4 md:px-6 py-10 grid md:grid-cols-2 gap-8 items-center" {...revealProps()}>
        <div>
          <p className="text-metros-red font-bold text-xs uppercase tracking-widest mb-1">{tx("about", "subtitle", "About")}</p>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl mb-3">{tx("about", "title", "More than a takeaway")}</h2>
          <p className="text-muted-foreground">{s.about}</p>
        </div>
        <div className="bg-[#F9F9F9] rounded-2xl p-6" data-testid="find-us">
          <h3 className="font-heading font-bold text-xl mb-4">Find us</h3>
          {(s.address || s.name) && (
            <a data-testid="find-us-map" target="_blank" rel="noopener noreferrer"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || s.name)}`}
              className="block rounded-xl overflow-hidden mb-4 border border-gray-100 group relative">
              <iframe title="METROS location" width="100%" height="180" style={{ border: 0, pointerEvents: "none" }} loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(s.address || s.name)}&z=15&output=embed`} />
              <span className="absolute bottom-2 right-2 bg-white/95 text-metros-black text-xs font-semibold rounded-full px-3 py-1 shadow flex items-center gap-1 group-hover:bg-metros-red group-hover:text-white transition-colors"><Navigation size={12} /> Open map</span>
            </a>
          )}
          <div className="space-y-3 text-sm">
            <p className="flex items-center gap-2"><MapPin size={16} className="text-metros-red" /> {s.address}</p>
            <p className="flex items-center gap-2"><Phone size={16} className="text-metros-red" /> {s.phone}</p>
            {s.email && <p className="flex items-center gap-2" data-testid="find-us-email"><Mail size={16} className="text-metros-red" /> {s.email}</p>}
            <p className="flex items-center gap-2"><Clock size={16} className="text-metros-red" /> Open today {s.opening_hours?.monday?.open}–{s.opening_hours?.monday?.close}</p>
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <a href={`tel:${s.phone}`} data-testid="call-metros" className="inline-flex items-center gap-2 bg-metros-black text-white rounded-full px-5 py-2.5 font-semibold"><Phone size={16} /> Call METROS</a>
            {(s.address || s.name) && (
              <a data-testid="home-get-directions" target="_blank" rel="noopener noreferrer"
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.address || s.name)}`}
                className="inline-flex items-center gap-2 bg-metros-red text-white rounded-full px-5 py-2.5 font-semibold"><Navigation size={16} /> Get Directions</a>
            )}
          </div>
        </div>
      </motion.section>
    ),
    final_cta: (
      <motion.section className="max-w-7xl mx-auto px-4 md:px-6 pb-12" {...revealProps()}>
        <div className="rounded-3xl bg-metros-red text-white p-10 md:p-14 text-center">
          <h2 className="font-heading font-extrabold text-3xl md:text-5xl">{tx("final_cta", "title", "Hungry? Let's fix that.")}</h2>
          <p className="text-white/90 mt-2">{tx("final_cta", "text", `Order now for ${s.delivery?.estimated_time} delivery or quick collection.`)}</p>
          <button data-testid="final-order-now" onClick={openOrderType}
            className="mt-6 bg-white text-metros-red rounded-full px-8 py-4 font-bold hover:scale-105 transition-transform">Order Now</button>
        </div>
      </motion.section>
    ),
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative text-white grain overflow-hidden mx-3 md:mx-6 mt-3 md:mt-4 rounded-xl md:rounded-2xl" style={{ backgroundColor: heroBgAlpha }}>
        <img src={hero.image} alt="" aria-hidden className="md:hidden absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="md:hidden absolute inset-0" style={{ background: `linear-gradient(to top, ${hero.bg_color || "#0A0A0A"} 15%, ${hero.bg_color || "#0A0A0A"}99 60%, ${hero.bg_color || "#0A0A0A"}66)` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-12 pb-5 md:py-8 grid md:grid-cols-2 gap-6 md:gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="font-heading font-extrabold text-lg sm:text-5xl lg:text-6xl leading-tight sm:leading-[0.95] tracking-tight mt-1 md:mt-0">
              {hero.headline || "GOOD FOOD. MADE FOR YOU."}
            </h1>
            <p className="text-sm md:text-base text-gray-300 mt-2 md:mt-3 max-w-lg">{hero.subtext}</p>
            <div className="mt-4 md:mt-6 flex flex-row md:flex-col items-center md:items-start justify-between gap-3 md:gap-0">
              <div data-testid="hero-status" className="order-1 md:order-2 md:mt-4 flex flex-col gap-1.5">
                {storeW.openNow ? (
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-green-400">
                    <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" /> Open now{storeW.close ? ` · until ${storeW.close}` : ""}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-red-300" data-testid="hero-closed-now">
                    <Clock size={14} /> Closed now{nextOpen ? ` · Opens ${nextOpen.dayLabel} at ${nextOpen.time}` : ""}
                  </span>
                )}
                {delivW.openNow ? (
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-green-400" data-testid="hero-delivery-open">
                    <span className="h-2 w-2 rounded-full bg-green-400" /> Delivering now{delivW.close ? ` · until ${delivW.close}` : ""}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-red-300" data-testid="hero-delivery-closed">
                    <Clock size={14} /> Delivery {windowLabel(delivW)}
                  </span>
                )}
                <div className="hidden md:flex items-center gap-2 mt-0.5" data-testid="hero-quick-actions">
                  <button data-testid="hero-schedule-btn" onClick={scheduleOrder}
                    className="text-xs font-bold rounded-full px-3 py-1.5 bg-metros-red text-white hover:bg-metros-redhover active:scale-95 transition-all">
                    Schedule
                  </button>
                  {storeW.openNow && (
                    <button data-testid="hero-collection-btn" onClick={() => startOrder("collection")}
                      className="text-xs font-bold rounded-full px-3 py-1.5 bg-white/10 text-white hover:bg-white/20 active:scale-95 transition-all">
                      Collection
                    </button>
                  )}
                </div>
              </div>
              <div className="order-2 md:order-1 flex md:flex-wrap gap-2.5">
                <button data-testid="hero-order-online" onClick={openOrderType}
                  className="text-metros-red font-bold flex items-center gap-2 px-1 py-2.5 text-sm active:scale-95 transition-all">
                  {hero.primary_cta || "Order Online"} <ArrowRight size={15} className="text-metros-red" />
                </button>
              </div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="relative w-full max-w-md ml-auto md:mr-0 md:translate-x-6 hidden md:block">
            <img src={hero.image} alt="METROS food" className="w-full h-44 md:h-56 object-cover rounded-3xl shadow-2xl" />
          </motion.div>
        </div>
      </section>

      {/* POSTCODE BAR — straddles the hero bottom edge */}
      <div className="relative z-30 -mt-2 md:-mt-12">
        <BranchSuggestBar />
      </div>

      {/* Branch Switch Prompt — remembered branch closed on return */}
      {selectedBranch && branchStatus?.is_open === false && !switchDismissed && (
        <div data-testid="branch-switch-prompt" className="max-w-7xl mx-auto px-4 md:px-6 -mt-4 md:-mt-8 relative z-30">
          <div className="bg-white border-2 border-amber-200 shadow-lg rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0" />
            <span className="flex-1 text-sm font-semibold text-metros-black">Your branch <b>{selectedBranch.name}</b> is closed right now{branchStatus.opens_at ? ` · opens at ${branchStatus.opens_at}` : ""}. Want to switch to an open one?</span>
            <button data-testid="switch-branch-btn" onClick={() => setBranchOpen(true)} className="bg-metros-red hover:bg-metros-redhover text-white rounded-full px-4 py-2 text-sm font-bold transition-colors">Choose branch</button>
            <button data-testid="switch-branch-dismiss" onClick={() => setSwitchDismissed(true)} className="text-gray-400 hover:text-metros-black text-sm font-semibold">Dismiss</button>
          </div>
        </div>
      )}

      {orderedKeys.map((k) => {
        if (!(isVisible(k) && sectionNodes[k])) return null;
        const c = secByKey[k] || {};
        const hasColor = c.bg_color && /^#[0-9a-fA-F]{3,8}$/.test(c.bg_color);
        const hasText = c.text_color && /^#[0-9a-fA-F]{3,8}$/.test(c.text_color);
        const overlay = Math.max(0, Math.min(80, Number(c.overlay) || 0));
        const showOverlay = !!c.bg_image && overlay > 0;
        const padTop = Number(c.pad_top) || 0;
        const padBottom = Number(c.pad_bottom) || 0;
        const style = {};
        if (hasColor) style.backgroundColor = c.bg_color;
        if (c.bg_image) { style.backgroundImage = `url("${c.bg_image}")`; style.backgroundSize = "cover"; style.backgroundPosition = "center"; }
        if (hasText) style.color = c.text_color;
        if (padTop > 0) style.paddingTop = `${padTop}px`; else if (padTop < 0) style.marginTop = `${padTop}px`;
        if (padBottom > 0) style.paddingBottom = `${padBottom}px`; else if (padBottom < 0) style.marginBottom = `${padBottom}px`;
        const hasBg = hasColor || !!c.bg_image;
        const needsWrapper = hasBg || hasText || padTop !== 0 || padBottom !== 0 || showOverlay;
        if (!needsWrapper) return <div key={k}>{sectionNodes[k]}</div>;
        if (showOverlay) style.position = "relative";
        return (
          <div key={k} style={style} data-hp-text={hasText ? "1" : undefined}>
            {showOverlay && <div aria-hidden style={{ position: "absolute", inset: 0, background: `rgba(0,0,0,${overlay / 100})`, pointerEvents: "none", zIndex: 0 }} />}
            {showOverlay ? <div style={{ position: "relative", zIndex: 1 }}>{sectionNodes[k]}</div> : sectionNodes[k]}
          </div>
        );
      })}

      <ProductModal product={modal} open={!!modal} onClose={() => setModal(null)} />
      <BranchSelect open={branchOpen} onClose={() => setBranchOpen(false)} goToMenuOnSelect />
    </div>
  );
}
